/*
  Seamless collection filtering — progressive enhancement over the section's GET
  form. Facet / sort / chip / pagination changes fetch the re-rendered section via
  the Section Rendering API and swap the grid, facets, chips and count in place,
  updating history so URLs stay shareable. Falls back to full-page navigation when
  JS is off (the forms and links are real). Also owns the mobile filter drawer and
  the 2/3 column toggle (persisted to localStorage).

  All control listeners are delegated on the section root because [data-facets]
  (and the facet form inside it) is replaced on every swap — direct listeners on
  the form would be lost after the first fetch.
*/

const COLS_KEY = 'rhode:collection-cols'
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

document.querySelectorAll('[data-collection]').forEach((root) => {
  const sectionId = root.dataset.sectionId
  let baseUrl = root.dataset.collectionUrl || window.location.pathname // mutated by the sale toggle (path-based tag filter)
  const grid = root.querySelector('[data-grid]')
  if (!grid) return

  const facetForm = () => root.querySelector('[data-facet-form]') // re-queried: replaced on swap

  let debounce
  let latest = 0 // guards against out-of-order responses

  // ── Build the query string from the facet form (sort_by radios live inside it)
  const buildParams = () => {
    const params = new URLSearchParams()
    const form = facetForm()
    if (form) {
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() === '') return // drop empty price inputs
        params.append(key, value)
      })
    }
    return params.toString()
  }

  // ── Fetch the re-rendered section and swap the live fragments ──────────────
  const swap = (doc, selector) => {
    const target = root.querySelector(selector)
    const source = doc.querySelector(selector)
    if (target && source) target.innerHTML = source.innerHTML
  }

  const render = async (paramString, { push = true, scrollToTop = false } = {}) => {
    const token = ++latest
    grid.setAttribute('aria-busy', 'true')

    const url = `${baseUrl}?section_id=${encodeURIComponent(sectionId)}${paramString ? '&' + paramString : ''}`
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const text = await res.text()
      if (token !== latest) return // a newer request already won

      const doc = new DOMParser().parseFromString(text, 'text/html')
      // swap inner HTML so outer elements (and the grid's inline --per-row) persist
      swap(doc, '[data-grid]')
      // keep the drawer's accordions as the user left them — the re-rendered
      // markup ships them collapsed, which reads as the drawer "closing"
      const openStates = [...root.querySelectorAll('[data-facets] details')].map((d) => d.open)
      swap(doc, '[data-facets]')
      ;[...root.querySelectorAll('[data-facets] details')].forEach((d, i) => { if (openStates[i] !== undefined) d.open = openStates[i] })
      swap(doc, '[data-chips]')
      swap(doc, '[data-count]')

      // sale takeover class + switch live outside the swapped fragments — sync from the path
      syncSaleUI(paramString)

      // page height just changed (grid swap, sale padding/margins) — scroll-linked
      // animations (footer reveal) must re-measure or they park mid-tween
      window.dispatchEvent(new Event('layout:refresh'))

      if (push) {
        const newUrl = paramString ? `${baseUrl}?${paramString}` : baseUrl
        history.pushState({ collection: true }, '', newUrl)
      }
      if (scrollToTop) {
        root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
      }
    } catch (err) {
      // network/parse failure: fall back to a full navigation so the user isn't stuck
      window.location.href = paramString ? `${baseUrl}?${paramString}` : baseUrl
    } finally {
      if (token === latest) grid.removeAttribute('aria-busy')
    }
  }

  // Flip the sale takeover class + switch state to match baseUrl (idempotent)
  const syncSaleUI = (paramString) => {
    const onSale = /\/sale\/?$/.test(baseUrl)
    root.classList.toggle('collection-rhode--sale', onSale)
    const saleLink = root.querySelector('[data-sale-toggle]')
    if (saleLink) {
      const base = baseUrl.replace(/\/sale\/?$/, '')
      saleLink.href = (onSale ? base : base + '/sale') + (paramString ? '?' + paramString : '')
      saleLink.setAttribute('aria-checked', String(onSale))
      saleLink.querySelector('.collection-rhode__switch').classList.toggle('collection-rhode__switch--on', onSale)
    }
  }

  const applyControls = () => {
    clearTimeout(debounce)
    debounce = setTimeout(() => render(buildParams()), 300)
  }

  // ── Delegated control changes (facets + sort) ──────────────────────────────
  root.addEventListener('change', (e) => {
    if (e.target.closest('[data-facet-form]')) applyControls()
  })
  root.addEventListener('input', (e) => {
    if (e.target.type === 'number' && e.target.closest('[data-facet-form]')) applyControls()

    // dual-handle price slider: clamp the pair, mirror into the number inputs, apply
    const range = e.target.closest('[data-price-range]') && e.target
    if (range && range.type === 'range') {
      const wrap = range.closest('[data-price-range]')
      const min = wrap.querySelector('[data-range="min"]')
      const max = wrap.querySelector('[data-range="max"]')
      if (+min.value > +max.value) (range === min ? max : min).value = range.value
      const details = wrap.closest('details')
      const minField = details.querySelector('[data-price="min"]')
      const maxField = details.querySelector('[data-price="max"]')
      minField.value = +min.value > 0 ? min.value : ''
      maxField.value = +max.value < +max.max ? max.value : ''
      applyControls()
    }
  })
  root.addEventListener('submit', (e) => {
    if (!e.target.closest('[data-facet-form], [data-sort]')) return
    e.preventDefault()
    clearTimeout(debounce)
    render(buildParams())
    closeDrawer()
  })

  // ── Chip removal, "clear all", pagination, drawer controls (delegated click)
  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-filter-open]')) return openDrawer()
    if (e.target.closest('[data-filter-close], [data-drawer-backdrop]')) return closeDrawer()

    // sale toggle: flip the base path (native tag filtering) and re-render in place.
    // The takeover flips immediately so the knob slide and the gsap colour sweep run
    // together, without waiting on the fetch.
    const sale = e.target.closest('a[data-sale-toggle]')
    if (sale) {
      e.preventDefault()
      const prev = {
        backgroundColor: getComputedStyle(root).backgroundColor,
        color: getComputedStyle(root).color,
      }
      baseUrl = new URL(sale.href, location.origin).pathname
      const params = buildParams()
      syncSaleUI(params) // knob starts sliding now (its own CSS transition)
      if (window.gsap && !reduceMotion) {
        // sweep the page colours from the old scheme to the new class's values
        window.gsap.from(root, {
          ...prev,
          duration: 0.5,
          ease: 'power2.inOut',
          clearProps: 'backgroundColor,color',
          overwrite: true,
        })
      }
      render(params)
      return
    }

    const link = e.target.closest('[data-chips] a, [data-pagination] a, a[data-clear]')
    if (link) {
      e.preventDefault()
      const params = link.search ? link.search.replace(/^\?/, '') : ''
      const isPage = !!e.target.closest('[data-pagination]')
      render(params, { scrollToTop: isPage })
    }
  })

  // ── Back / forward ─────────────────────────────────────────────────────────
  window.addEventListener('popstate', () => {
    baseUrl = window.location.pathname // sale toggle changes the path, not just the query
    render(window.location.search.replace(/^\?/, ''), { push: false })
  })

  // ── Mobile filter drawer ────────────────────────────────────────────────────
  const openBtn = root.querySelector('[data-filter-open]')
  const backdrop = root.querySelector('[data-drawer-backdrop]')
  function openDrawer() {
    root.setAttribute('data-drawer-open', '')
    if (backdrop) backdrop.hidden = false
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true')
  }
  function closeDrawer() {
    root.removeAttribute('data-drawer-open')
    if (backdrop) backdrop.hidden = true
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false')
  }
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer() })

  // ── Column toggle (persisted) ───────────────────────────────────────────────
  const viewToggle = root.querySelector('[data-view-toggle]')
  const buttons = viewToggle ? Array.from(viewToggle.querySelectorAll('button')) : []
  const applyCols = (cols) => {
    grid.style.setProperty('--per-row', cols)
    grid.dataset.cols = cols // CSS hook — dense view hides editorial tiles
    buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.cols === String(cols))))
  }
  if (buttons.length) {
    let saved = localStorage.getItem(COLS_KEY)
    if (saved && !buttons.some((b) => b.dataset.cols === saved)) saved = null // stale value from an old toggle set
    const initial = saved || grid.style.getPropertyValue('--per-row').trim() || buttons[0].dataset.cols
    applyCols(initial)
    // FLIP the grid between column counts (hand-rolled — no gsap Flip plugin needed)
    const animateCols = (cols) => {
      if (!window.gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return applyCols(cols)
      const cards = Array.from(grid.children)
      const before = cards.map((el) => el.getBoundingClientRect())
      applyCols(cols)
      cards.forEach((el, i) => {
        const a = before[i]
        const b = el.getBoundingClientRect()
        if (!b.width) return // hidden in this view (e.g. editorial tiles in dense mode)
        if (!a.width) {
          // newly revealed → simple fade/scale in
          window.gsap.fromTo(el, { opacity: 0, scale: 0.96 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'power3.out', clearProps: 'all' })
          return
        }
        window.gsap.fromTo(
          el,
          { x: a.left - b.left, y: a.top - b.top, scaleX: a.width / b.width, scaleY: a.height / b.height, transformOrigin: 'top left' },
          { x: 0, y: 0, scaleX: 1, scaleY: 1, duration: 0.55, ease: 'power3.inOut', clearProps: 'transform', overwrite: true }
        )
      })
    }
    viewToggle.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-cols]')
      if (!btn || btn.dataset.cols === grid.dataset.cols) return
      animateCols(btn.dataset.cols)
      localStorage.setItem(COLS_KEY, btn.dataset.cols)
    })
  }

  // ── Phone column toggle: 1 or 2 up (CSS reads data-mcols under 750px) ────────
  const mToggle = root.querySelector('[data-view-toggle-mobile]')
  if (mToggle) {
    const MKEY = 'rhode:collection-mcols'
    const mButtons = Array.from(mToggle.querySelectorAll('button'))
    const applyM = (n) => {
      grid.dataset.mcols = n
      mButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mcols === String(n))))
    }
    let savedM = null
    try { savedM = localStorage.getItem(MKEY) } catch (_) {}
    applyM(savedM === '1' ? '1' : '2')
    mToggle.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-mcols]')
      if (!btn) return
      applyM(btn.dataset.mcols)
      try { localStorage.setItem(MKEY, btn.dataset.mcols) } catch (_) {}
    })
  }
})
